import React, { useState, useEffect, useCallback } from "react";
import Layout from "../../layout/AppLayout";
import { fetchSolvingProcessesMarketplace } from "../../services/accessAPI";
import { Select, Button, message, Spin, Card, Row, Col  } from "antd";
import DynamicFormWizard from "./DynamicFormWizard";
import pic from "../../../assets/img/MARCIN_1.JPG";
import { useNavigate } from "react-router-dom";

const { Option } = Select;

const WizardSolvingProcesses = () => {
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [interfaceFrom, setInterfaceFrom] = useState(null);
  const [interfaceTo, setInterfaceTo] = useState(null);
  const [realWorldProblem, setRealWorldProblem] = useState(null);
  const [uniqueInterfaceFromOptions, setUniqueInterfaceFromOptions] = useState([]);
  const [uniqueInterfaceToOptions, setUniqueInterfaceToOptions] = useState([]);
  const [filteredRealWorldProblems, setFilteredRealWorldProblems] = useState([]);
  const [endpointDescriptions, setEndpointDescriptions] = useState([]);
  const [selectedEndpoint, setSelectedEndpoint] = useState(null); 
  const [noMatchesMessage, setNoMatchesMessage] = useState(""); 

  // Fetch data on component mount
  useEffect(() => {
    const loadApiConfig = async () => {
      try {
        const apiList = await fetchSolvingProcessesMarketplace();
        if (apiList) {
          setData(apiList);
        } else {
          message.warning("No data available.");
        }
      } catch (error) {
        console.error("Error fetching API list:", error);
        message.error("Failed to load marketplace data.");
      } finally {
        setLoading(false);
      }
    };
    loadApiConfig();
  }, []);

  // Extract unique InterfaceFrom and InterfaceTo options
  useEffect(() => {
    const interfaceToSet = new Set();
    const interfaceFromSet = new Set();

    data.forEach((item) => {
      item.intermodalInterfaces?.forEach((interfaceObj) => {
        if (interfaceObj.interfaceFrom) {
          interfaceFromSet.add(interfaceObj.interfaceFrom.toUpperCase());
        }
        if (interfaceObj.interfaceTo) {
          interfaceToSet.add(interfaceObj.interfaceTo.toUpperCase());
        }
      });
    });

    setUniqueInterfaceFromOptions([...interfaceFromSet]);
    setUniqueInterfaceToOptions([...interfaceToSet]);
  }, [data]);
  
  // Filter Real World Problems based on InterfaceFrom and InterfaceTo
  const filterRealWorldProblems = useCallback(() => {
    const problemsSet = new Set(); 

    data.forEach((item) => {
      const matches = item.intermodalInterfaces?.some(
        (interfaceObj) =>
          interfaceObj.interfaceFrom.toUpperCase() === interfaceFrom.toUpperCase() &&
          interfaceObj.interfaceTo.toUpperCase() === interfaceTo.toUpperCase()
      );
      if (matches) {
        item.realWorldProblems?.forEach((problem) => {
          problemsSet.add(problem);
        });        
      }
    });
    const filteredProblems = [...problemsSet];

    if (filteredProblems.length === 0) {
      setNoMatchesMessage("No matching real-world problems found. Please select other options."); // Set message state
      setRealWorldProblem(null);
      setSelectedEndpoint(null);
    } else {
      setNoMatchesMessage(""); // Clear message when there are matches
    }
    
    setFilteredRealWorldProblems(filteredProblems);
  }, [data, interfaceFrom, interfaceTo]);

  // Update Endpoint Descriptions when Real World Problem changes
  useEffect(() => {
    if (realWorldProblem) {
      const uniqueDescriptions = new Set();
      const descriptions = [];

      data.forEach((item) => {
        const matches = item.realWorldProblems?.some((problem) => problem === realWorldProblem);

        if (matches) {
          item.endpoints?.forEach((endpoint) => {
            if (!uniqueDescriptions.has(endpoint.description)) {
              uniqueDescriptions.add(endpoint.description);
              // Include basePath from the parent API (item)
              descriptions.push({
                method: endpoint.method,
                path: endpoint.path,
                description: endpoint.description,
                basePath: item.baseUrl,
                expectedInput: endpoint.expectedInput,
                name: item.name,
              });
            }
          });
        }
      });

      setEndpointDescriptions(descriptions);
    } else {
      setEndpointDescriptions([]);
    }
  }, [realWorldProblem, data]);

    useEffect(() => {
    if (!selectedEndpoint) return;
    const hasIframeFormat = selectedEndpoint.expectedInput.some((input) => input.format === "iframe");

    if (hasIframeFormat) {
      navigate("/SolverIframe", { state: { url: selectedEndpoint.basePath } });
    }
  }, [selectedEndpoint]);
 
  return (
    <Layout>
      <h2 style={{ color: "#001529", textAlign: "left", marginBottom: 20 }}>Intermodal Interface Wizard</h2>
      <Row gutter={[16, 16]}>
        <Col xs={24} md={12}>
          <Card
            style={{
              height: "80vh",               
              padding: 20,
              borderRadius: 10,
              boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
              backgroundColor: "#ffffff",
              display: "flex",
              flexDirection: "column", 
              overflow: 'auto'
            }}
            // Optionally, you can also style the card body:
            styles={{ body: { flexGrow: 1, display: "flex", flexDirection: "column" } }}
          >
            <div style={{ flexGrow: 1 }}>
              {loading ? (
                <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}>
                  <Spin spinning={loading} fullscreen />
                </div>
              ) : (
                <div>
                  {/* InterfaceFrom Dropdown */}
                  <p>Please choose an Intermodal Interface for your preferred transportation service.</p>
                  <Select
                    style={{ width: "100%", marginBottom: 50 }}
                    value={interfaceFrom}
                    onChange={(value) => setInterfaceFrom(value)}
                    placeholder="Select Interface From"
                  >
                    {uniqueInterfaceFromOptions.map((option) => (
                      <Option key={option} value={option}>
                        {option}
                      </Option>
                    ))}
                  </Select>

                  {/* InterfaceTo Dropdown */}
                  <Select
                    style={{ width: "100%", marginBottom: 40 }}
                    value={interfaceTo}
                    onChange={(value) => setInterfaceTo(value)}
                    placeholder="Select Interface To"
                  >
                    {uniqueInterfaceToOptions.map((option) => (
                      <Option key={option} value={option}>
                        {option}
                      </Option>
                    ))}
                  </Select>

                  {/* Next Button */}
                  <Button
                    type="primary"
                    onClick={filterRealWorldProblems}
                    disabled={!interfaceFrom || !interfaceTo}
                    style={{ marginBottom: 20 }}
                  >
                    Next
                  </Button>
                  {noMatchesMessage && <p style={{color: 'red'}}>{noMatchesMessage}</p>}
                  {/* Real World Problems Dropdown */}

                  {filteredRealWorldProblems.length > 0 && (
                    <>
                      <p>Please select a Real-world Multimodal Transport Problem</p>
                      <Select
                        style={{ width: "100%", marginBottom: 10 }}
                        value={realWorldProblem}
                        onChange={(value) => setRealWorldProblem(value)}
                        placeholder="Select Real World Problem"
                      >
                        {filteredRealWorldProblems.map((problem, index) => (
                          <Option key={index} value={problem}>
                            {problem}
                          </Option>
                        ))}
                      </Select>
                    </>
                  )}

                  {/* Endpoint Descriptions Dropdown */}
                  {endpointDescriptions.length > 0 && (
                    <div style={{ marginTop: 20 }}>
                      <p>Please select a Solving Process</p>
                      <Select
                        style={{ width: "100%", marginBottom: 10 }}
                        placeholder="Select an Endpoint Description"
                        onChange={(value) => {
                          // Find the selected endpoint object using the description
                          const selected = endpointDescriptions.find((ep) => ep.description === value);
                          setSelectedEndpoint(selected); // Store the selected endpoint
                        }}
                      >
                        {endpointDescriptions.map((endpoint, index) => (
                          <Option key={index} value={endpoint.description}>
                            {endpoint.description}
                          </Option>
                        ))}
                      </Select>
                    </div>
                  )}
                </div>
              )}
            </div>
          </Card>
        </Col>
        <Col xs={24} md={12}>
          {!selectedEndpoint ? (
            <img
              src={pic}
              alt="Transportation"
              className="bounce-in"
              style={{ width: "100%", height: "80vh", borderRadius: 10 }}
            />
          ) : (
              <DynamicFormWizard apiData={selectedEndpoint} pageToNavigate="WizardSolvingProcesses" />            
          )}
        </Col>
      </Row>
    </Layout>
  );
};

export default WizardSolvingProcesses;
